import { encounterReplay } from './encounter-replay';
import { admitTruthWire, ownsTruthOperation } from './truth-transport';
import { reloadRefusedEncounter } from './truth-refusal-notice';
import { applyFrameComputed } from '../janus-engine/transport/apply-frame-computed';
import { expandAppliedFrameInputs, type AppliedFrameResult } from '../janus-engine/transport/applied-frame';
import type { MovementTruthRow } from '../janus-engine/movement/client-truth-commit';
import { createAppliedFrameParts } from '../janus-engine/transport/applied-frame-parts';
// router.ts — message dispatcher.
//
// Routes parsed WebSocket payloads to the right handler. After the 7th carve-out
// (2026-05-22), 13 of the 15 message types are dispatched to handlers inside
// THIS bundle. The 2 remaining template.js-resident handlers
// (handleEncounterUpdate, handleProgressUpdate) are still ambient-declared in
// globals.d.ts pending future extraction.
//
// The `_exhaustive: never` guard at the end of the switch turns "PHP publisher
// added a new type but no JS handler exists yet" into a compile-time error:
// the new arm in WSMessage (types.ts) won't be covered by the switch, so the
// `parsedData` flowing into the default branch isn't `never`, and the
// assignment fails to typecheck. Add a case → error goes away. Drop a case →
// error appears.

import type { WSMessage } from './types';
import { withActionTimingEcho } from '../shared/action-timing';
import { handleBasePatch, handleComputedReady } from './base-patch';
import { handleMagicTarget } from './magic-target';
import { handleMovementTruth, confirmedRevFor } from './movement-truth';
import { handleObjectManipulation } from './object-manipulation';
import { handleActionTruth } from './action-truth';
import { handleAttackTruth } from './attack-truth';
import { handleAbilityTruth } from './ability-truth';
import { handleResourceTruth } from './resource-truth'; // L3: GM / sheet resource tools
import { handleLongRestTruth, type LongRestWire } from './long-rest-truth';
import { handleStatusTruth } from './status-truth'; // GMS: GM status tools (triggers, statuses)
import { handleZoneTruth } from './zone-truth'; // MV2: persistent-zone move / resize
import { handleSpellPrepTruth } from './spell-prep-truth'; // L7: prepared-spell list writes
import { chargeTruthFrameIsNewer, handleChargeTruth } from './charge-truth'; // ROLLS S3: GM / sheet charge tools (window.ftTryCommitCharge)
import { handleTurnTruth } from './turn-truth';
import { handleInventoryDestroyTruth } from './inventory-destroy-truth'; // R1: registers window.ftTryCommitObjectDestruction
import { handleInventoryFloorMoveTruth } from './inventory-floor-move-truth'; // I1: registers window.ftTryCommitFloorInventoryMove
import { handleInventoryFlagTruth } from './inventory-flag-truth'; // INV2: wares / hidden flag frames; registers window.ftCommitInventoryFlag
import { handleInventoryEquipTruth } from './inventory-equip-truth'; // I2/I3 S2: equip-family frames; registers window.ftCommitInventoryEquip
import { handleInventoryTradeTruth } from './inventory-trade-truth'; // INV2: trade / haggle / training exchange frames
import { handleMinigameTruth } from './minigame-truth'; // L5: registers window.ftTryCommitUseKey (keys / lockpicking commits)
import { handleTrapTruth } from './trap-truth'; // TRAPS: registers window.ftTryCommitTrapVerb (arm / discern commits)
import { handleTimeTruth } from './time-truth';
import { handleCombatTruth } from './combat-truth'; // T2/T3: start / exit combat boundary frames // T4: registers window.ftTryCommitTimePush; the clock rides campaign_update, T4b's removals ride janus_time_truth
import { isJSON } from './utils';
import { handleRollInsert } from './roll-insert';
import { handleMagicArea } from './magic-area';
import { handleRollGroupComplete } from './roll-group';
import { handleRollUpdate } from './roll-update';
import { handleCampaignUpdate } from './campaign-update';
import { adoptRelocationNavigation } from '../shared/geometry-relocation-delivery';
import { handleCasterUpdate } from './caster-update';
import { handleCasterUpdateDelta } from './caster-delta';
import { handleEncounterUpdate } from './encounter-update';
import { handleEncounterUpdateDelta } from './encounter-delta';
import { handleEncounterStatsUpdate } from './encounter-stats-update';
import { handleTurnTimerUpdate } from './turn-timer-update';
import { handleCampaignTransportUpdate } from './campaign-transport-update';
import { handlePlayerEncounterTransfer } from './player-encounter-transfer';
import { handleProgressUpdate } from './progress-update';
import { handleFt3dPing } from './ft3d-ping';
import { handleFt3dChat, handleFt3dEmoteCue } from './ft3d-chat';
import {
    handleFt3dAbilityTargetingPreview,
    handleFt3dAbilityTargetingPreviewClear,
} from './ft3d-ability-targeting';
import {
    handleFt3dAoePlacementPreview,
    handleFt3dAoePlacementPreviewClear,
} from './ft3d-aoe-placement';
import { handleFt3dMovePreview, handleFt3dMovePreviewClear } from './ft3d-move';
import {
    handleFt3dDirectorSpotlightUpdate,
    handleFt3dDirectorSpotlightClear,
} from './ft3d-director-spotlight';
import { handleFt3dDoorState, handleFt3dDoorPeek } from './ft3d-door-state';
import { refreshFt3dCurtains } from './ft3d-curtain-refresh';
import { handleFt3dDoorOpening } from './ft3d-door-opening';
import { handleFt3dPlayerPresence } from './ft3d-player-presence';
import { handleFt3dPartyVote } from './ft3d-party-vote';
import { handleMinimapGraphChanged } from './minimap-graph-changed';
import { markFt3dEventDelayDiagnostic } from '../shared/ft3d-event-delay-diagnostic';
import { noteWireEncounterFrame } from './encounter-mutation-generation';
import { encounterCreatureField } from '../shared/array-types';

function processWebSocketMessage(data: unknown): void {
    markFt3dEventDelayDiagnostic('parent.websocket.process-entry', data);
    let payload = data;
    if (typeof payload === 'string' && payload.startsWith('Echo: ')) {
        payload = payload.slice(6);
    }

    if (typeof payload !== 'string' || !isJSON(payload)) {
        ftDebugLog('ws', { nonJsonMessage: payload });
        return;
    }

    try {
        const parsedData: WSMessage | WSMessage[] = JSON.parse(payload);
        ftDebugLog('ws', () => ({ parsedJson: parsedData }));

        if (encounterReplay.receive(parsedData, frame => handleWebSocketMessage(frame as WSMessage | WSMessage[]))) return;
        void Promise.resolve(handleWebSocketMessage(parsedData)).catch(error => console.warn('[websocket] receive failed', error));
    } catch (e) {
        console.error('Error parsing WebSocket message as JSON:', e);
    }
}

let loggedTruthIdentityMismatch = false;
const receiveAppliedPart = createAppliedFrameParts();
let receiveTail: Promise<void> | undefined;
function handleWebSocketMessage(parsedData: WSMessage | WSMessage[]): void | Promise<void> {
    const apply = async (): Promise<void> => {
        for (const message of Array.isArray(parsedData) ? parsedData : [parsedData]) {
            if (message.type === 'janus_applied_part') {
                const complete = await receiveAppliedPart(message);
                if (complete) await applyWebSocketMessage(complete as WSMessage);
            } else await applyWebSocketMessage(message);
        }
    };
    const pending = receiveTail || Array.isArray(parsedData) || parsedData.type === 'janus_applied_part' ? (receiveTail ?? Promise.resolve()).then(apply)
        : applyWebSocketMessage(parsedData);
    if (!pending) return;
    const tail = Promise.resolve(pending).catch(error => console.warn('[websocket] ordered receive failed', error));
    receiveTail = tail;
    void tail.then(() => { if (receiveTail === tail) receiveTail = undefined; });
    return pending;
}
function applyWebSocketMessage(parsedData: WSMessage): void | Promise<void> {
    const wire = parsedData as unknown as { truth?: boolean; actor_id?: string; encounter_id?: string; operation_id?: string; applied_frame?: AppliedFrameResult;
        command?: { truth?: boolean; actor_id?: string; target_id?: string; encounter_id?: string; operation_id?: string; applied_frame?: AppliedFrameResult } };
    if ((['actor_id', 'encounter_id', 'operation_id'] as const).some(key => wire[key] !== undefined
        && wire.command?.[key] !== undefined && String(wire[key]).toUpperCase() !== String(wire.command[key]).toUpperCase()
        // Opportunity reactions ride the mover's envelope and carry the reactor in the command.
        && !(key === 'actor_id' && parsedData.type === 'janus_attack_truth'
            && String(wire.command.target_id ?? '').toUpperCase() === String(wire.actor_id).toUpperCase()))) {
        if (!loggedTruthIdentityMismatch) {
            loggedTruthIdentityMismatch = true;
            ftDebugLog('ws', { truthIdentityMismatch: parsedData.type });
        }
        return;
    }
    if (parsedData.type === 'janus_dice_truth' && parsedData.command.schema === 'janus.roll.truth.v1'
        && (wire.applied_frame || wire.command?.applied_frame)) return;
    const frame = wire.applied_frame ?? wire.command?.applied_frame;
    if (frame && (frame.encounter_id.toUpperCase() !== String(wire.encounter_id ?? wire.command?.encounter_id ?? '').toUpperCase()
        || frame.actor_id.toUpperCase() !== String(wire.actor_id ?? wire.command?.actor_id ?? '').toUpperCase()
        || frame.operation_id.toUpperCase() !== String(wire.operation_id ?? wire.command?.operation_id ?? '').toUpperCase())) return;
    // An encounter renderer owns the subscription; its parent owns the action
    // cache. Forward before local install/dedup, including before iframe load.
    if (parsedData.type === 'base_patch' || (wire.truth || wire.command?.truth) && (frame || parsedData.type === 'janus_trigger_prompt' || parsedData.type === 'janus_dice_truth')) {
        try {
            if (window.parent !== window && window.frameElement?.matches('[data-ft3d-encounter-frame]'))
                window.parent.dispatchEvent(new CustomEvent('ft:encounter-wire-received', { detail: { source: window, frame: parsedData } }));
        } catch { /* A cross-origin embed cannot share the parent's action cache. */ }
    }
    if (!admitTruthWire(parsedData as unknown as Record<string, unknown>)) return;
    if (parsedData.type === 'janus_dice_truth' && parsedData.command.schema === 'janus.charge.truth.v1'
        && !chargeTruthFrameIsNewer(parsedData.command as unknown as Parameters<typeof chargeTruthFrameIsNewer>[0])) {
        routeWebSocketMessage(parsedData);
        return;
    }
    if (frame && !('cache_ref' in frame) && frame.inputs?.new_entity_base) expandAppliedFrameInputs(frame);
    const correction = parsedData as unknown as { correction?: boolean; encounter_id?: string; operation_id?: string; command?: { correction?: boolean; encounter_id?: string; operation_id?: string } };
    const correctedEncounter = correction.encounter_id ?? correction.command?.encounter_id;
    let reloading: Promise<void> | undefined;
    if ((correction.correction || correction.command?.correction) && correctedEncounter
        && Array.isArray(window.FateTide?.encounterCache?.get(correctedEncounter)?.encounter_creatures_arr))
        reloading = reloadRefusedEncounter(correctedEncounter, correction.operation_id ?? correction.command?.operation_id);
    if ((wire.truth || wire.command?.truth) && frame && !ownsTruthOperation(frame.operation_id)) {
        const held = window.FateTide?.encounterCache?.get(frame.encounter_id)?.encounter_creatures_arr;
        applyFrameComputed(frame, id => (Array.isArray(held) ? held : []).find(row =>
            String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase() === id),
            () => {}, (id, row) => confirmedRevFor(frame.encounter_id, id, row as unknown as MovementTruthRow));
        window.dispatchEvent(new CustomEvent('ft:truth-frame-applied', { detail: frame }));
    }
    if ((wire.truth || wire.command?.truth) && frame && !correction.correction && !correction.command?.correction) {
        const next = { ...parsedData, command: { ...wire.command, applied_frame: frame, pushed: true } } as WSMessage;
        const paint = (): void | Promise<void> => routeWebSocketMessage(next);
        if (ownsTruthOperation(frame.operation_id)) return paint();
        return handleBasePatch({ type: 'base_patch', encounter_id: frame.encounter_id, ops: [], applied: JSON.stringify(frame) }).then(paint, paint);
    }
    const routed = routeWebSocketMessage(parsedData);
    return reloading ? Promise.all([routed, reloading]).then(() => {}) : routed;
}

function routeWebSocketMessage(parsedData: WSMessage): void | Promise<void> {
    markFt3dEventDelayDiagnostic('parent.websocket.router-entry', parsedData);
    // Derived-clock S2 (2026-09-04): a creature / encounter state frame means statuses or meters may have
    // changed since the GM tab's heartbeat embed was built — the campaign-clock runtime treats one as
    // "something may be due" (campaign-time-init/campaign-clock-runtime.ts). Fire-and-forget; no payload.
    switch (parsedData.type) {
        case 'base_patch':
        case 'caster_update':
        case 'caster_update_delta':
        case 'encounter_update':
        case 'encounter_update_delta':
        case 'encounter_stats_update':
            // Wire origin only: an in-flight encounter fetch that this frame may have overtaken refetches once
            // (encounter-mutation-generation.ts). Browser re-adoptions never pass through here.
            noteWireEncounterFrame(parsedData, casterId => encounterCreatureField(
                window.FateTide?.casterCache?.get(casterId)?.encounter_creatures, 'encounter_id'));
            try {
                window.dispatchEvent(new CustomEvent('ft:ws-state-frame', { detail: { type: parsedData.type } }));
            } catch (_) { /* listeners are optional */ }
            break;
        default:
            break;
    }
    // A resync-only correction on a page holding the roster already took the shared recovery above.
    // Applying it to both base runtimes would launch two more full reads and rebuild every scene input.
    // routeWebSocketMessage is outside handleWebSocketMessage's scope: re-derive, and skip only when that reload actually ran (held roster).
    const corrected = parsedData as unknown as { correction?: boolean; encounter_id?: string };
    if (parsedData.type === 'base_patch' && corrected.correction && corrected.encounter_id && !parsedData.ops.length
        && Array.isArray(window.FateTide?.encounterCache?.get(corrected.encounter_id)?.encounter_creatures_arr)
        && parsedData.resync?.every(id => id.toUpperCase() === corrected.encounter_id!.toUpperCase())) return;
    switch (parsedData.type) {
        case 'base_patch':                 return handleBasePatch(parsedData);
        case 'computed_ready':             void handleComputedReady(parsedData); break;
        case 'roll_insert':                handleRollInsert(parsedData); break;
        case 'magic_area':                 handleMagicArea(parsedData); break;
        case 'magic_target':               handleMagicTarget(parsedData); break;
        case 'roll_group_complete':        handleRollGroupComplete(parsedData); break;
        case 'janus_truth_presentation':    break; // Presented once at truth admission, including campaign-only viewers.
        case 'roll_update':                void handleRollUpdate(parsedData); break;
        case 'campaign_update':            handleCampaignUpdate(parsedData); break;
        case 'caster_update':              handleCasterUpdate(parsedData); break;
        case 'caster_update_delta':        handleCasterUpdateDelta(parsedData); break;
        case 'encounter_update':           return handleEncounterUpdate(parsedData);
        case 'encounter_update_delta':     return handleEncounterUpdateDelta(parsedData);
        case 'encounter_stats_update':     handleEncounterStatsUpdate(parsedData); break;
        case 'turn_timer_update':          handleTurnTimerUpdate(parsedData); break;
        case 'campaign_transport_update':  handleCampaignTransportUpdate(parsedData); break;
        case 'player_encounter_transfer':  void handlePlayerEncounterTransfer(parsedData); break;
        case 'progress_update':            handleProgressUpdate(parsedData); break;
        case 'janus_movement_truth':         withActionTimingEcho(parsedData.command, () => handleMovementTruth(parsedData.command)); break;
        case 'ft3d_object_manipulation':     handleObjectManipulation(parsedData.command); break;
        case 'janus_action_truth':           withActionTimingEcho(parsedData.command, () => handleActionTruth(parsedData.command)); break;
        case 'janus_attack_truth':           withActionTimingEcho(parsedData.command, () => handleAttackTruth(parsedData.command)); break;
        case 'janus_ability_truth':          withActionTimingEcho(parsedData.command, () => handleAbilityTruth(parsedData.command)); break;
        case 'janus_resource_truth':
            if ((parsedData.command as unknown as { kind?: string }).kind === 'long_rest') handleLongRestTruth(parsedData.command as unknown as LongRestWire);
            else handleResourceTruth(parsedData.command);
            break;
        case 'janus_status_truth':           handleStatusTruth(parsedData.command); break;
        case 'janus_spawn_truth':            break; // The shared AppliedFrame installer above owns newborns.
        case 'janus_inventory_truth': break; // The shared installer owns inventory deltas.
        case 'janus_geometry_relocation_truth': {
            const applied = parsedData.applied_frame ?? parsedData.command.applied_frame as import('../janus-engine/transport/applied-frame').AppliedFrame | undefined;
            if (applied && !('cache_ref' in applied)) adoptRelocationNavigation(applied);
            break;
        }
        case 'ft3d_room_generation_truth':    break; // The same installer owns the saved shape and inventory diff.
        case 'janus_applied_part':           break; // Reassembled before operation admission.
        case 'janus_zone_truth':             handleZoneTruth(parsedData.command); break;
        case 'janus_spell_prep_truth':       handleSpellPrepTruth(parsedData.command); break;
        case 'janus_dice_truth':
            if (parsedData.command.schema === 'janus.charge.truth.v1') handleChargeTruth(parsedData.command as unknown as Parameters<typeof handleChargeTruth>[0]);
            break;
        case 'janus_charge_truth':           handleChargeTruth(parsedData.command); break;
        case 'janus_turn_truth':             withActionTimingEcho(parsedData.command, () => handleTurnTruth(parsedData.command)); break;
        case 'janus_time_truth':             handleTimeTruth(parsedData.command); break;
        case 'janus_combat_truth':           handleCombatTruth(parsedData.command); break;
        case 'janus_inventory_destroy':      handleInventoryDestroyTruth(parsedData.command); break;
        case 'janus_inventory_floor_move':   handleInventoryFloorMoveTruth(parsedData.command); break;
        case 'janus_inventory_flag':         handleInventoryFlagTruth(parsedData.command); break;
        case 'janus_inventory_equip':        handleInventoryEquipTruth(parsedData.command); break;
        case 'janus_inventory_trade':        handleInventoryTradeTruth(parsedData.command); break;
        case 'janus_minigame_truth':         handleMinigameTruth(parsedData.command); break;
        case 'janus_trap_truth':             handleTrapTruth(parsedData.command); break;
        case 'ft3d_ping':                    handleFt3dPing(parsedData); break;
        case 'ft3d_chat':                    handleFt3dChat(parsedData); break;
        case 'ft3d_emote_cue':               handleFt3dEmoteCue(parsedData); break;
        case 'ft3d_ability_targeting_preview': handleFt3dAbilityTargetingPreview(parsedData); break;
        case 'ft3d_ability_targeting_preview_clear': handleFt3dAbilityTargetingPreviewClear(parsedData); break;
        case 'ft3d_aoe_placement_preview': handleFt3dAoePlacementPreview(parsedData); break;
        case 'ft3d_aoe_placement_preview_clear': handleFt3dAoePlacementPreviewClear(parsedData); break;
        case 'ft3d_move_preview':            handleFt3dMovePreview(parsedData); break;
        case 'ft3d_move_preview_clear':      handleFt3dMovePreviewClear(parsedData); break;
        case 'ft3d_director_spotlight_update': handleFt3dDirectorSpotlightUpdate(parsedData); break;
        case 'ft3d_director_spotlight_clear': handleFt3dDirectorSpotlightClear(parsedData); break;
        case 'ft3d_door_state':           handleFt3dDoorState(parsedData); break;
        case 'ft3d_door_peek':            handleFt3dDoorPeek(parsedData); break;
        case 'ft3d_curtain_changed':
            if (checkIfValidUUID(parsedData.encounter_id)) refreshFt3dCurtains(parsedData.encounter_id, parsedData.curtainDelta);
            break;
        case 'ft3d_door_opening':         handleFt3dDoorOpening(parsedData); break;
        case 'ft3d_player_presence':       handleFt3dPlayerPresence(parsedData); break;
        case 'ft3d_party_vote':            handleFt3dPartyVote(parsedData); break;
        case 'minimap_graph_changed':      handleMinimapGraphChanged(parsedData); break;
        // Owned by the encounter-overlays bundle (teleport-offers.ts), which loads after this one.
        case 'teleport_offers':            window.dispatchEvent(new CustomEvent('ft:teleport-offers', { detail: parsedData })); break;
        case 'session_reminder':           window.dispatchEvent(new CustomEvent('ft:session-reminder', { detail: parsedData })); break;
        // Owned by the PL healer (janus-engine / c1-possessor-shadow.ts, core + late bundles) — a window event keeps the bundles decoupled.
        case 'janus_pl_update':            window.dispatchEvent(new CustomEvent('ft:janus-pl-update', { detail: parsedData.command })); break;
        case 'janus_c1_verdict':          window.dispatchEvent(new CustomEvent('ft:janus-c1-verdict', { detail: parsedData.command })); break;
        // Owned by websocket/trigger-consent.ts (OWNER RULING 2026-10-02: optional triggers are a 5 s prompt to the controller).
        case 'janus_trigger_prompt':       window.dispatchEvent(new CustomEvent('ft:janus-trigger-prompt', { detail: parsedData })); break;
        default: {
            // Exhaustiveness guard: if a new WSMessage arm is added in types.ts
            // without a matching case here, TS fails with
            //   Type '...' is not assignable to type 'never'.
            const _exhaustive: never = parsedData;
            ftDebugLog('ws', { unknownType: (parsedData as { type: string }).type });
            return _exhaustive;
        }
    }
}

window.processWebSocketMessage = processWebSocketMessage;
window.handleWebSocketMessage = handleWebSocketMessage;

export { processWebSocketMessage, handleWebSocketMessage };
